@if (package$ | async; as package) {
	<div data-test-id="package-detail">
		<vp-carousel [items]="images" />
		<section class="section-spacing bg-white">
			<div class="section-inner section-gap flex-col">
				<div class="column-gap flex-col lg:flex-row">
					<div class="flex-col lg:w-2/3">
						<header data-test-class="package-detail-header">
							<h1
								class="heading-group heading-group-lg"
								data-test-class="title"
							>
								{{ package.name }}
							</h1>
							<p
								class="heading-group-overline"
								data-test-class="overline"
								role="doc-subtitle"
							>
								{{ 'global.package-label' | translate }}
							</p>
						</header>
						<vp-read-more>
							<markdown
								[data]="package.description"
								class="markdown block"
								data-test-class="package-detail-description"
							/>
						</vp-read-more>
						<h2
							class="running-head running-head-center"
							data-test-class="variants-header"
						>
							@if (package.packages.length > 1) {
								{{
									'storyblok.pages.choose-your-package'
										| translate
								}}
							} @else {
								{{ 'storyblok.pages.your-package' | translate }}
							}
						</h2>
						<vp-tab-menu
							[hideTabs]="package.packages.length <= 1"
							[useButtonVariant]="true"
							[(activeTab)]="activePackageIndex"
							color="white"
							data-test-id="package-variants"
						>
							@for (
								packageVariant of package.packages;
								track packageVariant.guid
							) {
								<vp-tab-menu-item
									[content]="activePackageVariant"
									[label]="packageVariant.name"
									[name]="'variant-' + ($index + 1)"
								>
									<p
										class="heading-group heading-group-sm font-semibold"
									>
										{{
											'storyblok.pages.package-includes'
												| translate
										}}
									</p>
									<markdown
										[data]="packageVariant.bulletContent"
										class="list-checkmarks markdown"
										data-test-id="package-detail-description"
									/>
								</vp-tab-menu-item>
							}
							<ng-template #activePackageVariant>
								<p
									class="heading-group heading-group-sm font-semibold"
								>
									{{
										'storyblok.pages.package-includes'
											| translate
									}}
								</p>
								<markdown
									[data]="
										package.packages[activePackageIndex()]
											?.bulletContent
									"
									class="list-checkmarks markdown"
									data-test-id="package-detail-description"
								/>
							</ng-template>
						</vp-tab-menu>
					</div>
					<div class="flex-col lg:w-1/3">
						@if (
							!package.isAvailableSoon &&
							!package.isExpired &&
							activePackage()
						) {
							<sb-availability-widget
								[bookingtoolUrl]="this.bookingtool"
								[currency]="hotelStore.currency()"
								[forceHideRangePrices]="true"
								[hotelGuid]="package.hotel.guid"
								[hotelSlug]="this.hotelSlug"
								[language]="language"
								[maxDate]="maxDate"
								[minDate]="minDate"
								[packageGuid]="
									activePackage().guid ?? activePackage().GUID
								"
								[packageSlug]="activePackage().shijiCode"
								[packages]="package.packages"
								[showPackages]="true"
								(packageSelected)="onPackageSelected($event)"
							>
								@if (activePackage()?.price.amount) {
									<vp-pricing
										[conditions]="
											(activePackage()?.price?.type ===
											'perPerson'
												? 'global.price-per-person'
												: 'global.total-price-label'
											) | translate
										"
										[currency]="
											activePackage().price.currency
										"
										[indication]="true"
										[isHorizontal]="false"
										[price]="
											activePackage().price.displayAmount
										"
										[pricingToolTip]="{
											dateRange:
												activePackage().price
													?.dateRange,
											total: activePackage().price
												?.amount,
											occupancy: pricingTooltipOccupancy,
											items: [
												{
													name: package.name,
													price: activePackage().price
														?.amountExcludingCharges,
												},
											],
											taxes: {
												tourist:
													activePackage().price
														?.charges,
											},
										}"
										alignment="self-start"
										data-test-class="package-price"
										size="lg"
									/>
								}
							</sb-availability-widget>
						} @else {
							<div
								class="card card-lg flex flex-col gap-4"
								data-test-class="active-package-card"
							>
								@if (
									package.isAvailableSoon &&
									!package.isExpired
								) {
									<ng-container
										[ngTemplateOutlet]="notAvailable"
										[ngTemplateOutletContext]="{
											title: 'storyblok.pages.package-available-soon.title',
											content:
												'storyblok.pages.package-available-soon.content',
										}"
									/>
								}
								@if (
									package.isExpired &&
									!package.isAvailableSoon
								) {
									<ng-container
										[ngTemplateOutlet]="notAvailable"
										[ngTemplateOutletContext]="{
											title: 'storyblok.pages.package-expired.title',
											content:
												'storyblok.pages.package-expired.content',
										}"
									/>
								}
							</div>
						}
						@if (package.hotel?.rating?.score) {
							<vp-rating
								[isHorizontal]="true"
								[numberOfRatings]="
									package.hotel.rating.numberOfRatings
								"
								[score]="package.hotel.rating.score"
							/>
						}
					</div>
				</div>
				<div class="column-gap flex-col lg:flex-row">
					<div class="flex-col lg:w-2/3">
						@if (package.hotel) {
							<h2
								class="running-head running-head-center"
								data-test-class="hotel-header"
							>
								{{
									'storyblok.pages.hotel-information'
										| translate
								}}
							</h2>
							@if (package.hotel.images?.length > 0) {
								<vp-mini-carousel
									[items]="hotelImages"
									aspectRatio="21:9"
								/>
							}
							<vp-tab-menu
								color="white"
								data-test-id="package-hotel-info"
							>
								<vp-tab-menu-item
									[content]="facilitiesRef"
									[label]="'global.facilities' | translate"
									name="facilities"
								/>
								<vp-tab-menu-item
									[content]="goodToKnowsRef"
									[label]="'global.good-to-know' | translate"
									name="good-to-knows"
								/>
								@if (
									this.hotelStore.address().latitude &&
									this.hotelStore.address().longitude
								) {
									<vp-tab-menu-item
										[content]="mapRef"
										[label]="'global.map' | translate"
										name="map"
									/>
								}
							</vp-tab-menu>
							<ng-template #facilitiesRef>
								<vp-list-grid
									[listItems]="
										package.hotel?.facilities || []
									"
									[name]="'hotel-facilities'"
									[sheetTitle]="
										('global.hotel.label' | translate) +
										' ' +
										('global.facilities' | translate)
									"
									[useCol2]="true"
								/>
							</ng-template>
							<ng-template #goodToKnowsRef>
								<vp-list-grid
									[listItems]="
										package.hotel?.goodToKnows || []
									"
									[name]="'hotel-good-to-knows'"
									[sheetTitle]="
										'global.good-to-know' | translate
									"
									[useCol2]="false"
								/>
							</ng-template>
							<ng-template #mapRef>
								<vp-map
									[lat]="this.hotelStore.address().latitude"
									[long]="this.hotelStore.address().longitude"
								/>
							</ng-template>
						}
						<h2
							class="running-head running-head-center"
							data-test-class="faq-header"
						>
							{{ 'storyblok.pages.faq-title' | translate }}
						</h2>
						<div
							class="flex flex-col gap-4"
							data-test-id="package-faqs"
						>
							<vp-accordion
								[isOpen]="true"
								[title]="
									'storyblok.pages.package-terms-and-conditions'
										| translate
								"
							>
								<markdown
									[data]="package.termsAndConditions"
									class="markdown"
								/>
							</vp-accordion>
							@for (faq of package.faqs; track $index) {
								<vp-accordion [title]="faq.question">
									<markdown
										[data]="faq.answer"
										class="markdown"
									/>
								</vp-accordion>
							}
						</div>
					</div>
					<div class="w-1/3"></div>
				</div>
			</div>
		</section>
	</div>
}

<ng-template #notAvailable let-content="content" let-title="title">
	<p
		[attr.data-test-id]="'package-no-available-title'"
		class="heading-group heading-group-sm font-semibold"
	>
		{{ title | translate }}
	</p>
	<p [attr.data-test-id]="'package-no-available-content'">
		{{ content | translate }}
	</p>
	<a
		[href]="parentUrl"
		class="btn btn-primary w-full"
		data-test-id="package-no-available-button"
	>
		{{ 'storyblok.pages.package.button-no-available' | translate }}
	</a>
</ng-template>
